import { type Component, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { useReportGeneration } from "@/domains/company/report/context/report-generation-provider";
import { REPORT_PHASES } from "@/domains/company/report/utils/report-phases";

/**
 * The minimized form of the loader, shown once the user has navigated away from
 * the route that started the generation.
 *
 * Why minimize rather than follow: someone who navigated away has just said
 * they want to do something else, so a modal that trails them would keep
 * blocking work they chose to start. But a loader that vanished entirely would
 * suggest the generation died with the page — which, since the state now lives
 * above the router, is no longer true. The indicator keeps the promise visible
 * without holding the page hostage: no scrim, no focus trap, click to reopen.
 */
export const ReportGenerationIndicator: Component = () => {
  const { t } = useI18n();
  const generation = useReportGeneration();
  const key = "profile.company.ats.report.loader";

  const visible = () =>
    generation.activeOffer() !== null && generation.isMinimized();

  const phase = () => REPORT_PHASES[generation.phaseIndex()];

  return (
    <Show when={visible()}>
      <button
        type="button"
        onClick={() => generation.restore()}
        aria-label={t(`${key}.reopen`) as string}
        class="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2.5 rounded-full border border-border bg-card/95 py-2 pr-4 pl-3 shadow-lg backdrop-blur transition-[box-shadow,scale] duration-200 ease-out-strong hover:shadow-xl active:scale-[0.98]"
      >
        <i class="ph ph-circle-notch report-loader-indicator-spin text-base text-primary" />
        <span class="flex min-w-0 flex-col items-start">
          <span class="text-xs font-semibold leading-tight">
            {t(`${key}.minimized`)}
          </span>
          <Show when={phase()}>
            <span class="max-w-[200px] truncate text-[11px] leading-tight text-muted-foreground">
              {t(`${key}.phases.${phase().key}.label` as never) as string}
            </span>
          </Show>
        </span>
      </button>
    </Show>
  );
};
